import { memo, useState } from 'react';
import classNames from '@rio-cloud/rio-uikit/classNames';

import ActionBarItem from '@rio-cloud/rio-uikit/ActionBarItem';
import useWindowResize from '@rio-cloud/rio-uikit/useWindowResize';
import Select from '@rio-cloud/rio-uikit/Select';
import Icon from '@rio-cloud/rio-uikit/Icon';
import { useUikitContext } from '../UikitContext';

/**
 * The versionsList.json has the following format:
 * {
 *     "id": "0.15.0",
 *     "label": "v0.15.0",
 *     "path": "0.15.0/"
 *     "url": "https://uikit.developers.rio.cloud/0.15.0/",
 *     "tag": "Beta"
 * }
 *
 * Use the tag property for things like "Beta" or alike.
 */

type VersionOption = {
    id: string;
    label: string;
    url: string;
    icon?: JSX.Element;
    selected?: boolean;
};

const getWindowWidth = (): number => window.innerWidth;

export const VersionSwitcher = memo(() => {
    const [windowWidth, setWindowWidth] = useState(getWindowWidth());
    const { selectedUikitVersion, versionsList } = useUikitContext();

    useWindowResize(() => {
        setWindowWidth(getWindowWidth());
    });

    const handleDesktopVersionSwitch = (item?: VersionOption) => {
        if (!item) {
            return;
        }
        // Keep deep linking while switching versions
        if (item.url) {
            const slash = item.url.endsWith('/') ? '' : '/';
            window.location.href = `${item.url}${slash}${window.location.hash}`;
        }
        console.log(`Switched to Version: ${item.label}`);
    };

    const handleMobileVersionSwitch = (url: string, label: string) => {
        // Keep deep linking while switching versions
        const slash = url.endsWith('/') ? '' : '/';
        window.location.href = `${url}${slash}${window.location.hash}`;
        console.log(`Switched to Version: ${label}`);
    };

    const displayedVersions = versionsList.versions.map(version => {
        const selected = version.id === selectedUikitVersion;

        return {
            ...version,
            selected,
        };
    });

    const options: VersionOption[] = displayedVersions.map(version => ({
        id: version.id,
        label: version.label,
        url: `/${version.path}`,
        icon: version.tag ? (
            <span className='label label-condensed label-filled label-muted'>{version.tag}</span>
        ) : undefined,
        selected: version.selected,
    }));

    const listItemClassnames = classNames(
        'display-flex padding-x-15 padding-y-10',
        'hover-bg-lightest cursor-pointer',
        'border border-bottom-only border-color-lighter last-child-border-bottom-none'
    );

    return (
        <>
            {windowWidth > 990 ? (
                <ActionBarItem
                    id='versions'
                    className='VersionSwitcher display-flex align-items-center width-auto margin-right-10'
                >
                    <Select
                        className='form-group margin-0 white-space-nowrap'
                        options={options}
                        pullRight
                        onChange={handleDesktopVersionSwitch}
                    />
                </ActionBarItem>
            ) : (
                <ActionBarItem id='versions' mobileDialogBodyClassName='padding-0'>
                    <ActionBarItem.Icon>
                        <Icon iconName='layer' />
                    </ActionBarItem.Icon>

                    <ActionBarItem.Popover
                        title='Choose a Version'
                        className='padding-0 max-height-300 overflow-y-auto'
                    >
                        {displayedVersions.map(version => (
                            <div
                                className={`${listItemClassnames} ${version.selected ? 'pointer-events-none' : ''}`}
                                key={version.id}
                                onClick={() => handleMobileVersionSwitch(version.url, version.label)}
                            >
                                <div className='flex-1-1 display-flex justify-content-between'>
                                    <div className='white-space-nowrap'>
                                        {version.selected && <span className='rio-glyph icon-ok margin-right-10' />}
                                        {version.label}
                                    </div>
                                    {version.tag && (
                                        <span className='label label-condensed label-filled label-muted margin-bottom-3'>
                                            {version.tag}
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </ActionBarItem.Popover>
                </ActionBarItem>
            )}
        </>
    );
});
